import React, {useState, useEffect} from "react";
import Select from "react-select";
import ModalDefault from "../../../common/modal/modal-default";
import ActionGroupService from "../../../../services/action-group-service";

const _ = require("lodash");
const Steve = require("@gluenetworks/steve_core").getInstance();
const PlatformSdk = require("@gluenetworks/platform-sdk-remote");
const SiteEntity = PlatformSdk.entities.ws.Site;

interface ROIFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApply: (filters: ROIFilterValues) => void;
    onReset?: () => void;
    initialFilters?: ROIFilterValues;
}

export interface ROIFilterValues {
    sites: SiteOption[];
    roles: RoleOption[];
}

interface SiteOption {
    value: string;
    label: string;
}

interface RoleOption {
    value: string;
    label: string;
}

// Custom styles for react-select
const customStyles = {
    multiValue: (styles: any, {data}: any) => {
        return {
            ...styles,
            backgroundColor: "#3498db",
            color: "white",
            padding: "4px 0 4px 10px",
            borderRadius: "8px",
        };
    },
    multiValueLabel: () => ({
        color: "white",
        fontSize: "12px",
        lineHeight: "15px",
    }),
    multiValueRemove: (styles: any) => ({
        ...styles,
        ":hover": {
            backgroundColor: "inherit !important",
        },
    }),
    menuPortal: (styles: any) => ({
        ...styles,
        zIndex: 9999,
    }),
    menu: (styles: any) => ({
        ...styles,
        zIndex: 9999,
    }),
};

const ROIFilterModal: React.FC<ROIFilterModalProps> = ({
    isOpen,
    onClose,
    onApply,
    onReset,
    initialFilters = {sites: [], roles: []},
}) => {
    const [filters, setFilters] = useState<ROIFilterValues>(initialFilters);
    const [availableSites, setAvailableSites] = useState<SiteOption[]>([]);
    const [availableRoles, setAvailableRoles] = useState<RoleOption[]>([]);
    const [loading, setLoading] = useState(false);

    // Load data when modal opens
    useEffect(() => {
        if (isOpen) {
            setFilters(initialFilters);
            loadData();
        }
    }, [isOpen, initialFilters]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Load sites data using SiteEntity.loadSites
            const tid = _.get(Steve, "userSession.activeOrganization.id");
            const sitePaths = await SiteEntity.loadSites(
                tid,
                {},
                {
                    id: true,
                    path: true,
                },
                {
                    path: 1,
                }
            );

            const sitesData: SiteOption[] = sitePaths
                .map(({id, path}: {id: string; path: string}) => ({
                    value: id,
                    label: path,
                }))
                .filter((item: SiteOption) => item.label !== undefined);

            setAvailableSites(sitesData);

            // Load roles data
            const rolesResponse = await ActionGroupService.getRolesForUserOrganizations();
            const rolesData: RoleOption[] = rolesResponse.map((role: any) => ({
                value: role.id, // Use ID as the value for filtering
                label: role.label, // Use label for display
            }));
            setAvailableRoles(rolesData);
        } catch (error) {
            console.error("Error loading filter data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSiteChange = (selectedSites: readonly SiteOption[] | null) => {
        setFilters((prev) => ({
            ...prev,
            sites: selectedSites ? [...selectedSites] : [],
        }));
    };

    const handleRoleChange = (selectedRoles: readonly RoleOption[] | null) => {
        setFilters((prev) => ({
            ...prev,
            roles: selectedRoles ? [...selectedRoles] : [],
        }));
    };

    const handleReset = () => {
        setFilters({sites: [], roles: []});
        if (onReset) {
            onReset();
        }
    };

    const handleApply = () => {
        onApply(filters);
        onClose();
    };

    const handleCancel = () => {
        setFilters(initialFilters);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <ModalDefault isOpen={isOpen}>
            <div className="glu-modal-content roi-filter-modal">
                <div className="glu-modal__header">
                    <div className="glu-modal__title glu-flex-grow">Filter</div>
                    <button type="button" className="glu-modal__close" onClick={handleCancel}>
                        <span className="g-icon g-icon-close" />
                    </button>
                </div>

                <div className="glu-modal__body">
                    <div className="roi-filter-modal__content">
                        {/* Sites Dropdown */}
                        <div className="roi-filter-modal__field">
                            {loading ? (
                                <div className="roi-filter-modal__loading">Loading sites...</div>
                            ) : (
                                <Select
                                    options={availableSites}
                                    value={filters.sites}
                                    onChange={handleSiteChange}
                                    isMulti
                                    isSearchable
                                    placeholder="Sites"
                                    className="glu-select glu-form-input-pad"
                                    classNamePrefix="react-select"
                                    styles={customStyles}
                                    closeMenuOnSelect={false}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            )}
                        </div>

                        {/* Roles Dropdown */}
                        <div className="roi-filter-modal__field">
                            {loading ? (
                                <div className="roi-filter-modal__loading">Loading roles...</div>
                            ) : (
                                <Select
                                    options={availableRoles}
                                    value={filters.roles}
                                    onChange={handleRoleChange}
                                    isMulti
                                    isSearchable
                                    placeholder="Roles"
                                    className="glu-select glu-form-input-pad"
                                    classNamePrefix="react-select"
                                    styles={customStyles}
                                    closeMenuOnSelect={false}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className="glu-modal__footer">
                    <button
                        type="button"
                        className="glu-btn glu-btn--secondary"
                        onClick={handleReset}
                    >
                        Reset
                    </button>
                    <button
                        type="button"
                        className="glu-btn glu-btn--secondary"
                        onClick={handleCancel}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="glu-btn glu-btn--primary"
                        onClick={handleApply}
                    >
                        Apply
                    </button>
                </div>
            </div>
        </ModalDefault>
    );
};

export default ROIFilterModal;
